import { useState, useMemo, useEffect } from "react"
import { useGetCategoriesQuery, useGetFoodItemsQuery } from "../services"

/**
 * Custom hook to debounce a value by a specified delay.
 */
function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function useMenuItems() {
  // State for selected category and search query
  const [activeCategory, setActiveCategory] = useState(undefined)
  const [searchQuery, setSearchQuery] = useState("")

  // Debounce the search query to avoid rapid-fire requests
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  // 1) load categories
  const { data: catRes, isLoading: loadingCats } = useGetCategoriesQuery()
  const categories = useMemo(() => {
    return catRes?.data?.records ?? []
  }, [catRes])

  // **NEW**: whenever `categories` changes, if nothing is selected yet, pick the first one
  useEffect(() => {
    if (!loadingCats && categories.length > 0 && activeCategory == null) {
      // find the first category whose active_status is true
      const firstActive = categories?.find(c => c.active_status);
      if (firstActive) {
        setActiveCategory(firstActive.name);
      }

      // setActiveCategory(categories[0]?.name)
    }
  }, [loadingCats, categories, activeCategory])

  // find the numeric ID for the a\ctive category
  const activeCategoryId = useMemo(() => {
    if (!activeCategory || !catRes?.data?.records) return undefined
    const found = catRes.data.records.find((c) => c.name === activeCategory)
    return found?.id
  }, [activeCategory, catRes])

  // const activeCategoryId = useMemo(() => {
  //   return categories.find(c => c.name === activeCategory)?.id
  // }, [categories, activeCategory])


  // 2) fetch items with params (use debounced search)
  const { data: itemsRes, isLoading: loadingItems, isFetching: isFetchingItems } = useGetFoodItemsQuery({
    category: activeCategoryId,
    data: 'active'
    // search: debouncedSearchQuery || undefined,
  })

  // 3) map into your UI shape
  const menuItems = useMemo(() => {
    const recs = itemsRes?.data?.records || []
    return recs.map((item) => ({
      id: item.id ?? item.name,
      name: item.name,
      price: item.price,
      image: item.image || "https://via.placeholder.com/150?text=Food+Item",
      description: item.description,
      type: item.item_type,
      hasAddOns: Array.isArray(item.groups) && item.groups.length > 0,
      groups: item.groups || [],
      primary_chef: item?.primary_chef,
      station: item?.stationData,
      allergies: item?.allergies
    }))
  }, [itemsRes])

  return {
    menuItems,
    categories,
    activeCategory,
    setActiveCategory,
    searchQuery,
    setSearchQuery,
    isFetchingItems,
    isLoading: loadingCats || loadingItems,
  }
}
